import { useState } from 'react';
import { assert } from '@chainflip/utils/assertion';
import { chainflipAssets, readAssetValue, type ChainflipAsset } from '@chainflip/utils/chainflip';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/shared/components';
import { DashboardModal } from '@/shared/components/dashboards/Modal';
import TokenInputField from '@/shared/components/TokenInputField';
import useToast from '@/shared/hooks/useToast';
import { CloseIcon } from '@/shared/icons/large';
import { TokenAmount, chainflipAssetMap } from '@/shared/utils';
import { useStateChainAccount } from '../../hooks/useStateChainAccount';

export default function ScheduleSwapModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const extrinsic = 'schedule-swap';
  const toast = useToast(extrinsic);
  const { account, sendExtrinsic } = useStateChainAccount();

  const [fromAsset, setFromAsset] = useState<ChainflipAsset>('Flip');
  const [toAsset, setToAsset] = useState<ChainflipAsset>('Btc');
  const [fromAmount, setFromAmount] = useState<TokenAmount>();

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      assert(fromAmount && fromAmount.gt(0), 'Amount must be greater than 0');
      await sendExtrinsic(extrinsic, fromAmount.toString(), fromAsset, toAsset, 100, '0');
    },
    onSuccess() {
      toast.success('Swap scheduled, radical!');
      onClose();
    },
    onError(err) {
      toast.error('Error while submitting transaction', { description: err.message });
    },
  });

  if (!account) return null;

  return (
    <DashboardModal open={open} onClose={onClose}>
      <div className="flex w-[487px] flex-col space-y-2.5">
        <div className="bg-holy-radial-gray-3-60 flex h-full w-full flex-col items-center justify-center gap-y-8 rounded-md border border-cf-gray-4 p-5 backdrop-blur-[6px]">
          <div className="flex w-full items-start justify-between">
            <div className="flex flex-1 flex-col space-y-2">
              <span className="cf-gray-gradient font-aeonikMedium text-20">Schedule swap</span>
              <div className="text-14 text-cf-light-2">Send it</div>
            </div>
            <button type="button" onClick={onClose} className="outline-cf-gray-5">
              <CloseIcon />
            </button>
          </div>
          <div className="flex w-full flex-col gap-y-2">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="from-select">Source asset</label>
            <select
              name="from-select"
              id="from-select"
              value={fromAsset}
              onChange={(e) => setFromAsset(e.target.value as ChainflipAsset)}
              className="my-2 py-1"
            >
              {chainflipAssets.map((asset) => (
                <option key={asset} value={asset}>
                  {asset}
                </option>
              ))}
            </select>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="to-select">Destination asset</label>
            <select
              name="to-select"
              id="to-select"
              value={toAsset}
              onChange={(e) => setToAsset(e.target.value as ChainflipAsset)}
              className="my-2 py-1"
            >
              {chainflipAssets.map((asset) => (
                <option key={asset} value={asset}>
                  {asset}
                </option>
              ))}
            </select>
            <TokenInputField
              value={fromAmount}
              onChange={setFromAmount}
              token={chainflipAssetMap[fromAsset]}
              label="Amount"
              balance={TokenAmount.fromAsset(
                readAssetValue(account.balances, fromAsset),
                fromAsset,
              )}
            />
            <Button
              loading={isPending}
              onClick={() => {
                mutate();
              }}
            >
              Send it
            </Button>
          </div>
        </div>
      </div>
    </DashboardModal>
  );
}
